import { useEffect, useState } from "react"
import { captureMessage } from "@sentry/react"
import parse from "html-react-parser"

import PurchaseModal from "app/components/subscriptions/purchase_modal"
import { Currency } from "app/components/subscriptions/currency_switcher_modal"
import Modal from "shared/components/modal"
import closeX from "images/components/x.svg?raw"
import proUpsellImage from "images/web/subscriptions/checkout_modal/applet-requires-pro-upsell-story.svg"
import proPlusUpsellImage from "images/web/subscriptions/checkout_modal/applet-requires-pro+-upsell-story.svg"
import { loadState, saveState } from "shared/localstorage_helpers"
import moment from "moment"

import styles from "./index.module.scss"

interface URLs {
  modalSubscriptionUrl: string
  newProSubscriptionUrl: string
  newProPlusSubscriptionUrl: string
}

interface SubscriptionProps {
  assets: {
    iftttSvgUrl: string
    proBadgeSvgUrl: string
    proPlusBadgeSvgUrl: string
  }
  browser_type: string
  create_stripe_customer_url: string
  currencies: Currency[]
  default_currency: string
  eligible_for_trial: boolean
  email: string
  initial_promo_code: string | null
  intermediate_pro: {
    product_id: string
    plan_id: string
    tier: string
    currency_plans: object
  }
  pro: {
    product_id: string
    plan_id: string
    tier: string
    currency_plans: object
  }
  subscriptions_path: string
  setup_intent_client_secret?: string | null
  setup_intent_return_url?: string | null
  stripe_customer_preinitialized?: boolean
}

interface Props {
  initialPromoCode: string
  modalCopyObj: {
    checkoutModalTarget: "intermediate_pro" | "pro"
    eligibleForTrialCopy: string
    copy: string
    title: string
  }
  onClose: () => void
  onUpgrade: (a: string) => void
  urls: URLs
  visible: boolean
}

const isRetryableModalFetchError = (error: unknown) => {
  const httpStatus = (error as { httpStatus?: number })?.httpStatus
  if (httpStatus != null && httpStatus >= 400 && httpStatus < 500) return false
  if (error instanceof SyntaxError) return false
  return true
}

const CheckoutModal = ({ modalCopyObj, onClose, onUpgrade, urls, visible, initialPromoCode }: Props) => {
  const [subscriptionProps, updateSubscriptionProps]: [SubscriptionProps, (a: SubscriptionProps) => void] =
    useState<SubscriptionProps>({} as SubscriptionProps)
  const [subscriptionPropsLoaded, setSubscriptionPropsLoaded]: [boolean, (a: boolean) => void] = useState(false)
  const { checkoutModalTarget, eligibleForTrialCopy, title } = modalCopyObj

  const checkoutStorageId = `checkoutModalSubscriptionProps-${window.App.user?.id}-v3`
  const loadCachedSubscriptionProps = () => JSON.parse(loadState(checkoutStorageId) || "null")
  const cacheSubscriptionProps = data => {
    saveState(
      JSON.stringify({
        ttl: moment().add(10, "minutes").unix(),
        data,
      }),
      checkoutStorageId
    )
  }

  const loadSubscriptionProps = async (attempt = 1) => {
    const maxAttempts = 2
    const cachedSubscriptionProps = loadCachedSubscriptionProps()

    if (cachedSubscriptionProps && moment().unix() < cachedSubscriptionProps.ttl) {
      if (window.App.env && window.App.allowDebugErrorMessages) {
        console.log("subscriptionProps loaded from cache", cachedSubscriptionProps)
      }
      updateSubscriptionProps(cachedSubscriptionProps.data)
      setSubscriptionPropsLoaded(true)
      return
    }

    try {
      const response = await fetch(urls.modalSubscriptionUrl, {
        method: "get",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        redirect: "error",
      })

      if (!response.ok) {
        throw Object.assign(new Error(`HTTP error ${response.status}`), { httpStatus: response.status })
      }

      const result = await response.json()
      updateSubscriptionProps(result)
      setSubscriptionPropsLoaded(true)
      cacheSubscriptionProps(result)
    } catch (error) {
      if (attempt < maxAttempts && isRetryableModalFetchError(error)) {
        await new Promise(resolve => setTimeout(resolve, 400))
        return loadSubscriptionProps(attempt + 1)
      }

      const modalTargetPlan = modalCopyObj.checkoutModalTarget
      const httpStatus = (error as { httpStatus?: number })?.httpStatus ?? null
      // In case that we have an issue load data to be used in SignUpAndPurchase, not block user. Redirect them to checkout page.
      captureMessage("Checkout modal failed to load subscription data and redirect user to checkout page.", {
        tags: { user_id: window.App.user?.id },
        extra: {
          modalTargetPlan,
          attempt,
          httpStatus,
          errorMessage: error instanceof Error ? error.message : String(error),
        },
      })
      window.location.assign(
        modalCopyObj.checkoutModalTarget === "intermediate_pro"
          ? urls.newProSubscriptionUrl
          : urls.newProPlusSubscriptionUrl
      )
    }
  }

  useEffect(() => {
    loadSubscriptionProps()
  }, [])

  useEffect(() => {
    if (visible) {
      window.App.Utils?.logCustomDatadogAction("stripe_checkout_initial")
      window.App.Utils?.logCustomDatadogAction("stripe_checkout_initial_modal")
    }
  }, [visible])

  return subscriptionPropsLoaded ? (
    <div className={`${styles["checkout-on-modal"]} checkout-on-modal`}>
      <Modal show={visible} includeCloseX={false}>
        <section className={styles["checkout-container"]}>
          <section className={styles["left"]}>
            <img
              className={styles["background-image"]}
              src={checkoutModalTarget === "pro" ? proPlusUpsellImage : proUpsellImage}
            />
          </section>

          <section className={styles["right"]}>
            <div className={styles["close"]} onClick={onClose}>
              <span className="close-x">{parse(closeX)}</span>
            </div>
            <header className={`${styles["onboarding-header"]} pb1 pb0--phone`}>
              <h1 className={styles["ifttt"]}>
                <img src={subscriptionProps["assets"].iftttSvgUrl} alt="IFTTT logo" width="122" height="37" />
                {checkoutModalTarget === "pro" ? (
                  <img src={subscriptionProps["assets"].proPlusBadgeSvgUrl} alt="Pro+ badge" width="91" height="53" />
                ) : (
                  <img src={subscriptionProps["assets"].proBadgeSvgUrl} alt="Pro badge" width="91" height="53" />
                )}
              </h1>
              <h2 className={`${styles["modal-title"]}`}>{title}</h2>
              {eligibleForTrialCopy ? <p className="subheader txt-body-3">{eligibleForTrialCopy}</p> : null}
            </header>
            <PurchaseModal
              onClose={onClose}
              onUpgrade={() => onUpgrade(checkoutModalTarget)}
              subscriptionProps={{
                ...subscriptionProps,
                initial_promo_code: initialPromoCode,
              }}
              target={checkoutModalTarget}
            />
          </section>
        </section>
      </Modal>
    </div>
  ) : null
}

export default CheckoutModal
